import * as React from 'react';
import styled, { css } from 'styled-components';
import { rem } from '../../tokens/spacing';

/** Matches legacy ListLineItemWrapper p-5 */
export const CRITERIA_TREE_INSET = rem(20);
/** Matches legacy ListLineItem top-12 — horizontal branch Y within each rule row */
export const CRITERIA_TREE_CONNECTOR_TOP = rem(48);
/** Legacy tree-view uses 1.5px for both spine width and branch height (not design-system rem scale). */
export const CRITERIA_TREE_LINE_THICKNESS = '1.5px';

const SPINE_HEIGHT_VAR = '--criteria-tree-spine-height';

const treeLineFill = css`
  display: block;
  background-color: var(--ui-border);
  pointer-events: none;
  flex-shrink: 0;
`;

const StyledTreeList = styled.div`
  position: relative;
  padding: ${CRITERIA_TREE_INSET};
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`;

/** Vertical spine ends at the last rule's junction (height set by layout measure). */
const StyledSpine = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: ${CRITERIA_TREE_LINE_THICKNESS};
  min-width: ${CRITERIA_TREE_LINE_THICKNESS};
  height: var(${SPINE_HEIGHT_VAR}, 0px);
  ${treeLineFill}
`;

const StyledTreeItem = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * Horizontal branch from spine into the rule card.
 * Extends ½ line thickness past the spine so the L joint is continuous (no hairline gap).
 */
const StyledConnector = styled.span`
  position: absolute;
  left: calc(-1 * ${CRITERIA_TREE_INSET} - (${CRITERIA_TREE_LINE_THICKNESS} / 2));
  top: ${CRITERIA_TREE_CONNECTOR_TOP};
  width: calc(${CRITERIA_TREE_INSET} + (${CRITERIA_TREE_LINE_THICKNESS} / 2));
  height: ${CRITERIA_TREE_LINE_THICKNESS};
  min-height: ${CRITERIA_TREE_LINE_THICKNESS};
  ${treeLineFill}
`;

function measureSpineHeight(listEl: HTMLElement): void {
  const items = listEl.querySelectorAll('[data-criteria-tree-item]');
  if (items.length === 0) {
    listEl.style.setProperty(SPINE_HEIGHT_VAR, '0px');
    return;
  }

  const lastItem = items[items.length - 1] as HTMLElement;
  const connector =
    lastItem.querySelector<HTMLElement>('[data-criteria-tree-connector]') ?? lastItem;

  const listTop = listEl.getBoundingClientRect().top;
  const junction = connector.getBoundingClientRect();
  const junctionCenterY = junction.top + junction.height / 2 - listTop;

  listEl.style.setProperty(SPINE_HEIGHT_VAR, `${Math.max(0, Math.round(junctionCenterY))}px`);
}

function useCriteriaTreeSpine(listRef: React.RefObject<HTMLDivElement | null>, itemCount: number): void {
  React.useLayoutEffect(() => {
    const listEl = listRef.current;
    if (!listEl) return;

    const update = () => {
      measureSpineHeight(listEl);
    };

    const observer = new ResizeObserver(update);
    observer.observe(listEl);

    const observeItems = () => {
      listEl.querySelectorAll('[data-criteria-tree-item]').forEach((node) => {
        observer.observe(node);
      });
    };

    observeItems();
    update();
    requestAnimationFrame(update);

    return () => {
      observer.disconnect();
    };
  }, [listRef, itemCount]);
}

export interface CriteriaRuleTreeListProps {
  semanticId?: string;
  /** Number of rule rows — remeasures spine when count changes */
  ruleCount: number;
  children: React.ReactNode;
}

export function CriteriaRuleTreeList({ semanticId, ruleCount, children }: CriteriaRuleTreeListProps) {
  const listRef = React.useRef<HTMLDivElement>(null);
  useCriteriaTreeSpine(listRef, ruleCount);

  return (
    <StyledTreeList ref={listRef} data-semantic-id={semanticId}>
      <StyledSpine aria-hidden />
      {children}
    </StyledTreeList>
  );
}

export interface CriteriaRuleTreeItemProps {
  children: React.ReactNode;
}

export function CriteriaRuleTreeItem({ children }: CriteriaRuleTreeItemProps) {
  return (
    <StyledTreeItem data-criteria-tree-item>
      <StyledConnector data-criteria-tree-connector aria-hidden />
      {children}
    </StyledTreeItem>
  );
}

CriteriaRuleTreeList.displayName = 'CriteriaRuleTreeList';
CriteriaRuleTreeItem.displayName = 'CriteriaRuleTreeItem';
